import {useCallback, useMemo} from 'react'
import {useDispatch} from 'react-redux'
import {
  getParametrizedHotelContext,
  getParametrizedHotelInfoContext
} from 'middleware/analytics/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {Hotel} from 'modules/sapiSearch/slice'

import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  AnalyticsContextPayload,
  Category,
  ClickType,
  Entity
} from '@vio/core/src/analytics/types/Events'

import {useShowAccommodationPage} from './useShowAccommodationPage'

export interface TrackUserHotelCardClickedParameters {
  hotelId: Hotel['objectID']
  component:
    | 'ListViewHotelCard'
    | 'MapViewHotelCard'
    | 'MobilePopUpDealModal'
    | 'Carousel'
    | 'StaticHotelCard'
    | 'ExperimentalBoVioFirstCardLayout' // Added as part of 34324324-mobile-hotel-card-bovio-first
  selectedElement: string
  analyticsContext?: Partial<Record<AnalyticsContext, AnalyticsContextPayload>>
  isTeamEvent?: boolean
  payload?: Record<string, unknown>
  // Added as part of dcbc5616-property-highlights-v1
  highlights?: string[]

  clickType?: ClickType
}

export const useHotelCardActions = () => {
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {showAccommodationPage} = useShowAccommodationPage()
  const handleTrackUserHotelCardClicked = useCallback(
    ({
      hotelId,
      component,
      selectedElement,
      analyticsContext,
      payload,
      highlights,
      clickType = ClickType.LeftClick,
      isTeamEvent = false
    }: TrackUserHotelCardClickedParameters) => {
      const dispatcher = isTeamEvent ? dispatchTeamEvent : dispatch

      const defaultAnalyticsContext = {
        [AnalyticsContext.HotelContext]: getParametrizedHotelContext(hotelId),
        [AnalyticsContext.HotelInfoContext]:
          getParametrizedHotelInfoContext(hotelId)
      }

      dispatcher(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelCard,
          action: Action.Clicked,
          analyticsContext: analyticsContext || defaultAnalyticsContext,
          component,
          payload: {
            selectedElement,
            // Added as part of dcbc5616-property-highlights-v1
            ...(highlights?.length ? {highlights} : {}),
            ...payload,

            clickType
          }
        })
      )
    },
    [dispatch, dispatchTeamEvent]
  )

  const handleShowHotelDetails = useCallback(
    (
      hotelId: Hotel['objectID'],
      // Added as a part of c747927f-deal-freeze-srp
      showDealFreezeToast: boolean = false
    ) => {
      showAccommodationPage({
        hotelId,
        showDealFreezeToast
      })
    },
    [showAccommodationPage]
  )

  const handleHideHotelDetails = useCallback(() => {
    dispatch(hideOverlay())
  }, [dispatch])

  const cardActions = useMemo(
    () => ({
      onShowHotelDetails: handleShowHotelDetails,
      onHideHotelDetails: handleHideHotelDetails,
      /**
       * Dispatches the `User_HotelCard_Clicked` tracking event
       */
      trackUserHotelCardClicked: handleTrackUserHotelCardClicked
    }),
    [
      handleShowHotelDetails,
      handleHideHotelDetails,
      handleTrackUserHotelCardClicked
    ]
  )

  return cardActions
}
